import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Grid,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Home as HomeIcon,
    Dashboard as DashboardIcon,
    SentimentDissatisfied as SadIcon
} from '@mui/icons-material';
import DynamicFooter from './DynamicFooter';

const NotFoundPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleGoToDashboard = () => {
        navigate('/dashboard');
    };

    const handleGoToHome = () => {
        navigate('/');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={24}
                    sx={{
                        p: { xs: 3, md: 6 },
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -50,
                            right: -50,
                            width: 200,
                            height: 200,
                            background: 'linear-gradient(45deg, #667eea55, #764ba255)',
                            borderRadius: '50%',
                            zIndex: 0
                        }
                    }}
                >
                    {/* Animated Icon */}
                    <Box
                        sx={{
                            position: 'relative',
                            mb: 4,
                            animation: 'bounce 2s infinite',
                            '@keyframes bounce': {
                                '0%, 20%, 50%, 80%, 100%': {
                                    transform: 'translateY(0)'
                                },
                                '40%': {
                                    transform: 'translateY(-20px)'
                                },
                                '60%': {
                                    transform: 'translateY(-10px)'
                                }
                            }
                        }}
                    >
                        <SadIcon
                            sx={{
                                fontSize: 100,
                                color: theme.palette.primary.main,
                                opacity: 0.8
                            }}
                        />
                    </Box>

                    {/* Main Content */}
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            fontSize: { xs: '4rem', sm: '6rem', md: '8rem' },
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 2,
                            fontFamily: '"Montserrat", sans-serif'
                        }}
                    >
                        404
                    </Typography>

                    <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                            mb: 3,
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontFamily: '"Poppins", sans-serif'
                        }}
                    >
                        Oops! Page Not Found
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            mb: 4,
                            color: theme.palette.text.secondary,
                            fontSize: '1.1rem',
                            maxWidth: '500px',
                            mx: 'auto',
                            lineHeight: 1.6
                        }}
                    >
                        The page you're looking for seems to have wandered off into the digital void. 
                        Don't worry, let's get you back on track!
                    </Typography>

                    {/* Action Buttons */}
                    <Grid
                        container
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: 4 }}
                    >
                        <Grid item>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGoToDashboard}
                                startIcon={<DashboardIcon />}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                                        boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.3s ease'
                                    }
                                }}
                            >
                                Go to Dashboard
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={handleGoToHome}
                                startIcon={<HomeIcon />}
                                sx={{
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.dark,
                                        color: theme.palette.primary.dark,
                                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.3s ease'
                                    }
                                }}
                            >
                                Back to Home
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Decorative Elements */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -30,
                            left: -30,
                            width: 150,
                            height: 150,
                            background: 'linear-gradient(45deg, #764ba255, #667eea555)',
                            borderRadius: '50%',
                            zIndex: 0
                        }}
                    />
                </Paper>

                {/* Footer Text with dynamic year */}
                <DynamicFooter companyName="Royal Gym" showHelpText={true} />
            </Container>
        </Box>
    );
};

export default NotFoundPage;